/**
 * Formulaire d'évaluation owner → renter avec stepper
 * Permet au propriétaire d'évaluer le locataire après une réservation complétée
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Rating,
  TextField,
  Stack,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  alpha,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  CheckCircle as CheckIcon,
  Build as EquipmentIcon,
  Schedule as PunctualityIcon,
  Chat as CommunicationIcon,
  Assignment as RulesIcon,
  Inventory as ReturnIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from '../../services/api';

const criteriaConfig = {
  equipment_respect: { icon: EquipmentIcon, color: '#2196f3' },
  punctuality: { icon: PunctualityIcon, color: '#4caf50' },
  communication: { icon: CommunicationIcon, color: '#ff9800' },
  return_state: { icon: ReturnIcon, color: '#9c27b0' },
  rules_respect: { icon: RulesIcon, color: '#f44336' }
};

const RenterEvaluationForm = ({ open, onClose, reservation, onSuccess, existingReview = null }) => {
  const theme = useTheme();
  const { t } = useTranslation('renterEvaluation');
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditMode = Boolean(existingReview);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État des notes (étape 1) - pré-rempli si mode édition
  const [ratings, setRatings] = useState(() => {
    if (existingReview?.criteria_scores) {
      return {
        equipment_respect: existingReview.criteria_scores.equipment_respect?.rating || 0,
        punctuality: existingReview.criteria_scores.punctuality?.rating || 0,
        communication: existingReview.criteria_scores.communication?.rating || 0,
        return_state: existingReview.criteria_scores.return_state?.rating || 0,
        rules_respect: existingReview.criteria_scores.rules_respect?.rating || 0
      };
    }
    return {
      equipment_respect: 0,
      punctuality: 0,
      communication: 0,
      return_state: 0,
      rules_respect: 0
    };
  });

  // État des commentaires (étape 2) - pré-rempli si mode édition
  const [comments, setComments] = useState(() => {
    if (existingReview?.criteria_scores) {
      return {
        equipment_respect: existingReview.criteria_scores.equipment_respect?.comment || '',
        punctuality: existingReview.criteria_scores.punctuality?.comment || '',
        communication: existingReview.criteria_scores.communication?.comment || '',
        return_state: existingReview.criteria_scores.return_state?.comment || '',
        rules_respect: existingReview.criteria_scores.rules_respect?.comment || ''
      };
    }
    return {
      equipment_respect: '',
      punctuality: '',
      communication: '',
      return_state: '',
      rules_respect: ''
    };
  });

  // État global (étape 3) - pré-rempli si mode édition
  const [overallRating, setOverallRating] = useState(existingReview?.rating || 0);
  const [overallComment, setOverallComment] = useState(existingReview?.comment || '');
  const [wouldRecommend, setWouldRecommend] = useState(
    existingReview?.criteria_scores?.would_recommend !== undefined 
      ? String(existingReview.criteria_scores.would_recommend) 
      : null
  );

  // État photos (étape 4) - pré-rempli si mode édition
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState(() => {
    if (existingReview?.equipment_photos && Array.isArray(existingReview.equipment_photos)) {
      return existingReview.equipment_photos; // URLs directement en mode édition
    }
    return [];
  });
  const [photoError, setPhotoError] = useState(null);

  const steps = [
    t('steps.ratings'),
    t('steps.comments'),
    t('steps.photos'),
    t('steps.summary')
  ];

  const handleNext = () => {
    // Validation étape 1
    if (activeStep === 0) {
      const allRated = Object.values(ratings).every(r => r > 0);
      if (!allRated) {
        setError(t('validation.allRatingsRequired'));
        return;
      }
    }

    // Validation étape 4 (summary)
    if (activeStep === 3) {
      if (overallRating === 0) {
        setError(t('validation.overallRatingRequired'));
        return;
      }
    }

    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      setError(t('validation.overallRatingRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construire criteria_scores pour le backend
      const criteriaScores = {};
      Object.keys(ratings).forEach(key => {
        criteriaScores[key] = {
          rating: ratings[key],
          comment: comments[key] || null
        };
      });

      const payload = {
        rating: overallRating,
        comment: overallComment || null,
        criteria_scores: criteriaScores,
        would_recommend: wouldRecommend,
        equipment_photos: photoPreviews.length > 0 ? photoPreviews : []
      };

      let responseData;
      if (isEditMode && existingReview) {
        // Mode édition : PUT pour mettre à jour
        const res = await api.put(`/reviews/owner-renter/${existingReview.id}`, payload);
        responseData = res.data?.data?.data ?? res.data?.data ?? null;
      } else {
        // Mode création : POST
        const res = await api.post(`/reviews/renter/${reservation.id}`, payload);
        responseData = res.data?.data?.data ?? res.data?.data ?? null;
      }

      toast.success(t('success'));

      if (onSuccess) {
        onSuccess(responseData);
      }

      handleClose();
    } catch (err) {
      console.error('Error submitting renter review:', err);
      const errorMsg = err.response?.data?.message || t('error');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setActiveStep(0);
      setRatings({
        equipment_respect: 0,
        punctuality: 0,
        communication: 0,
        return_state: 0,
        rules_respect: 0
      });
      setComments({
        equipment_respect: '',
        punctuality: '',
        communication: '',
        return_state: '',
        rules_respect: ''
      });
      setOverallRating(0);
      setOverallComment('');
      setWouldRecommend(null);
      setPhotoFiles([]);
      setPhotoPreviews([]);
      setPhotoError(null);
      setError(null);
      onClose();
    }
  };

  // Étape 1 : Notes
  const renderRatingsStep = () => (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ratingsStep.description')}
      </Typography>

      {Object.keys(criteriaConfig).map((key) => {
        const IconComponent = criteriaConfig[key].icon;
        return (
          <Paper
            key={key}
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: ratings[key] > 0 
                ? alpha(criteriaConfig[key].color, 0.05)
                : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: alpha(criteriaConfig[key].color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconComponent sx={{ color: criteriaConfig[key].color }} />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t(`criteria.${key}.title`)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {t(`criteria.${key}.description`)}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Rating
                    value={ratings[key]}
                    onChange={(e, newValue) => {
                      setRatings(prev => ({ ...prev, [key]: newValue || 0 }));
                      setError(null);
                    }}
                    size="large"
                    sx={{ '& .MuiRating-iconFilled': { color: criteriaConfig[key].color } }}
                  />
                  {ratings[key] > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t(`ratingLabels.${ratings[key]}`)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );

  // Étape 2 : Commentaires
  const renderCommentsStep = () => (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('commentsStep.description')}
      </Typography>

      {Object.keys(criteriaConfig).map((key) => {
        const IconComponent = criteriaConfig[key].icon;
        return (
          <Paper
            key={key}
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start" mb={1.5}>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  backgroundColor: alpha(criteriaConfig[key].color, 0.1)
                }}
              >
                <IconComponent sx={{ fontSize: 18, color: criteriaConfig[key].color }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {t(`criteria.${key}.title`)}
                </Typography>
                <Rating value={ratings[key]} readOnly size="small" />
              </Box>
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={t(`commentsPlaceholder.${key}`)}
              value={comments[key]}
              onChange={(e) => {
                if (e.target.value.length <= 300) {
                  setComments(prev => ({ ...prev, [key]: e.target.value }));
                }
              }}
              variant="outlined"
              size="small"
              helperText={t('charCount', { count: comments[key].length, max: 300 })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </Paper>
        );
      })}
    </Stack>
  );

  // Étape 3 : Photos (optionnel)
  const renderPhotosStep = () => {
    const handlePhotoUpload = (event) => {
      const files = Array.from(event.target.files);
      setPhotoError(null);

      if (photoPreviews.length + files.length > 5) {
        setPhotoError(t('photos.maxError', { max: 5 }));
        return;
      }

      const validFiles = files.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          setPhotoError(t('photos.sizeError', { max: '5MB' }));
          return false;
        }
        if (!file.type.startsWith('image/')) {
          setPhotoError(t('photos.formatError'));
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      // Ajouter les fichiers de façon synchrone (préserve l'ordre de sélection)
      setPhotoFiles(prev => [...prev, ...validFiles]);

      // Ajouter les aperçus de façon asynchrone via FileReader
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    };

    const handleRemovePhoto = (index) => {
      setPhotoFiles(prev => prev.filter((_, i) => i !== index));
      setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
      setPhotoError(null);
    };

    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('photos.description', { max: 5 })}
          </Typography>
          
          {photoError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhotoError(null)}>
              {photoError}
            </Alert>
          )}

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={photoPreviews.length >= 5}
            fullWidth
            sx={{ mb: 2 }}
          >
            {t('photos.addButton', { count: photoPreviews.length, max: 5 })}
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
            />
          </Button>
        </Box>

        {photoPreviews.length > 0 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('photos.addedLabel')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {photoPreviews.map((preview, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    component="img"
                    src={preview}
                    alt={`Photo ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePhoto(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.8)'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Stack>
        )}

        {photoPreviews.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              border: `2px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.02)
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('photos.noneAdded')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('photos.optionalHint')}
            </Typography>
          </Paper>
        )}
      </Stack>
    );
  };

  // Étape 4 : Résumé et recommandation
  const renderSummaryStep = () => (
    <Stack spacing={3}>
      {/* Note globale */}
      <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('overallRating.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {t('overallRating.description')}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Rating
            value={overallRating}
            onChange={(e, newValue) => {
              setOverallRating(newValue || 0);
              setError(null);
            }}
            size="large"
            sx={{ '& .MuiRating-iconFilled': { color: theme.palette.warning.main } }}
          />
          {overallRating > 0 && (
            <Typography variant="h6" color="warning.dark">
              {overallRating}/5
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Commentaire global */}
      <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('overallComment.label')}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={t('overallComment.placeholder')}
          value={overallComment}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              setOverallComment(e.target.value);
            }
          }}
          variant="outlined"
          helperText={t('charCount', { count: overallComment.length, max: 500 })}
        />
      </Paper>

      {/* Recommandation */}
      <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('recommendation.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {t('recommendation.question')}
        </Typography>
        <RadioGroup
          value={wouldRecommend}
          onChange={(e) => setWouldRecommend(e.target.value === 'true')}
        >
          <FormControlLabel 
            value={true} 
            control={<Radio color="success" />} 
            label={t('recommendation.yes')} 
          />
          <FormControlLabel 
            value={false} 
            control={<Radio color="error" />} 
            label={t('recommendation.no')} 
          />
        </RadioGroup>
      </Paper>

      {/* Résumé des notes */}
      <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
        <Typography variant="caption" fontWeight={600} color="primary" gutterBottom display="block">
          {t('summary.ratingsTitle')}
        </Typography>
        <Stack spacing={0.5}>
          {Object.keys(ratings).map(key => (
            <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption">{t(`criteria.${key}.title`)}</Typography>
              <Rating value={ratings[key]} readOnly size="small" />
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Résumé photos */}
      {photoPreviews.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
          <Typography variant="caption" fontWeight={600} color="info.main" gutterBottom display="block">
            {t('photos.summaryCount', { count: photoPreviews.length })}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            {photoPreviews.slice(0, 3).map((preview, index) => (
              <Box
                key={index}
                component="img"
                src={preview}
                alt={`Photo ${index + 1}`}
                sx={{
                  width: 50,
                  height: 50,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`
                }}
              />
            ))}
            {photoPreviews.length > 3 && (
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  +{photoPreviews.length - 3}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { borderRadius: fullScreen ? 0 : 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {isEditMode ? t('editTitle') : t('title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isEditMode ? t('editSubtitle') : t('subtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 400 }}>
          {activeStep === 0 && renderRatingsStep()}
          {activeStep === 1 && renderCommentsStep()}
          {activeStep === 2 && renderPhotosStep()}
          {activeStep === 3 && renderSummaryStep()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('buttons.cancel')}
        </Button>

        <Box sx={{ flex: 1 }} />

        {activeStep > 0 && (
          <Button
            startIcon={<PrevIcon />}
            onClick={handleBack}
            disabled={loading}
          >
            {t('buttons.previous')}
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleNext}
            disabled={loading}
          >
            {t('buttons.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            onClick={handleSubmit}
            disabled={loading || overallRating === 0}
          >
            {loading ? t('loading') : t('buttons.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RenterEvaluationForm;
